import { supabase } from '@/lib/supabase';
import { Student } from '@/types/models';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

export const studentService = {
  /**
   * Add a new student
   */
  addStudent: async (student: Omit<Student, 'id' | 'created_at'>): Promise<Student> => {
    try {
      // Convert dateOfBirth to date_of_birth for Supabase
      const dbStudent = {
        family_id: student.family_id,
        organization_id: student.organization_id,
        first_name: student.first_name,
        last_name: student.last_name,
        preferred_name: student.preferred_name,
        date_of_birth: student.date_of_birth,
        gender: student.gender,
        notes: student.notes,
        address_line1: student.address_line1,
        address_line2: student.address_line2,
        city: student.city,
        state: student.state,
        zip: student.zip,
        phone: student.phone,
        email: student.email,
        location_id: student.location_id,
        profile_picture_url: student.profile_picture_url,
        start_date: student.start_date,
        drop_date: student.drop_date,
        is_active: student.is_active !== false,
        no_public_photos: student.no_public_photos
      };

      const { data, error } = await supabase
        .from('students')
        .insert([dbStudent])
        .select()
        .single();

      if (error) throw error;
      
      return data as Student;
    } catch (error) {
      console.error('Error adding student:', error);
      toast.error('Failed to add student');
      throw error;
    }
  },
  
  /**
   * Upload a student profile picture to Supabase storage
   */
  uploadProfilePicture: async (file: File, organizationId: string, studentId: string): Promise<string> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${studentId}-${uuidv4()}.${fileExt}`;
      const filePath = `${organizationId}/students/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (uploadError) throw uploadError;
      
      // Get the public URL
      const { data } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);
      
      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error('Failed to upload profile picture');
      throw error;
    }
  },
  
  /**
   * Update an existing student
   */
  updateStudent: async (studentId: string, updateData: Partial<Student>): Promise<Student> => {
    try {
      const { data, error } = await supabase
        .from('students')
        .update(updateData)
        .eq('id', studentId)
        .select()
        .single();
        
      if (error) throw error;
      
      return data as Student;
    } catch (error) {
      console.error('Error updating student:', error);
      toast.error('Failed to update student');
      throw error;
    }
  },
  
  /**
   * Delete a student
   */
  deleteStudent: async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('students')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Failed to delete student');
      return false;
    }
  },

  /**
   * Get all students for an organization
   */
  getAllStudentsForOrganization: async (organizationId: string): Promise<Student[]> => {
    try {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .eq('organization_id', organizationId)
        .order('first_name', { ascending: true });
      if (error) throw error;
      return data as Student[];
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to fetch students');
      return [];
    }
  }
};

export default studentService;
